<script>
// @ts-check

import {
  canOpenAccount,
  createAuthorizationUrl,
  createIdentificationUrl,
  getBusinessAccountOnboarding
} from '@/api/businessAccount'
import { getPaymentTerminalIntegrations } from '@/api/paymentTerminals'
import BaseAdvertisement from '@/components/base/BaseAdvertisement.vue'
import BaseAlert from '@/components/base/BaseAlert.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseCustomModal from '@/components/base/BaseCustomModal.vue'
import BaseIcon from '@/components/base/BaseIcon.vue'
import LoadingSpinner from '@/components/base/LoadingSpinner.vue'
import BusinessAccountOnboardingForm from '@/components/business-account/BusinessAccountOnboardingForm.vue'
import { useRestrictions } from '@/core/composables/useRestrictions'
import { useVuelidate } from '@/core/composables/useVuelidate'
import { swanOnboardingState } from '@/core/constants'
import { useLanguageStore } from '@/store/language'
import { mapState } from 'pinia'
import { inject } from 'vue'
import Vuex from 'vuex'

export default {
  name: 'BusinessAccountInformationDialog',
  components: {
    BaseAlert,
    BaseCustomModal,
    BaseAdvertisement,
    BusinessAccountOnboardingForm,
    LoadingSpinner,
    BaseIcon,
    BaseButton
  },
  setup() {
    return {
      ...useRestrictions(canOpenAccount),
      ...useVuelidate(),
      isMobileAppAndIos: inject('isMobileAppAndIos', false)
    }
  },
  data: () => ({
    showOnboardingForm: false,
    onboardingLink: '',
    isLoading: true,
    paymentTerminals: []
  }),
  computed: {
    ...mapState(useLanguageStore, ['language']),
    ...Vuex.mapGetters('companyData', [
      'answerKycByDate',
      'canApplyForBusinessAccount',
      'isSwanVerificationNotStarted'
    ]),
    ...Vuex.mapGetters('app', ['isTmiService']),
    zettleConnected() {
      return this.paymentTerminals?.some((terminal) => {
        return terminal.name === 'izettle' && terminal.state === 'connected'
      })
    },
    requirementTexts() {
      return this.isTmiService
        ? this.$tm(
            `business_account.dialog_advertisement.content.requirements_content_tmi`
          )
        : this.$tm(
            `business_account.dialog_advertisement.content.requirements_content_oy`
          )
    }
  },
  async created() {
    this.isLoading = true

    try {
      const { data: onboarding } = await getBusinessAccountOnboarding()

      if (onboarding.onboarding_state === swanOnboardingState.ONGOING) {
        await this.handleOngoingOnboarding()
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error(error)
      }
    }

    this.isLoading = false

    // Fetch payment terminal information for showing info. No harm if this call fails
    try {
      this.paymentTerminals = await getPaymentTerminalIntegrations()
    } catch (error) {
      console.error(error)
    }
  },
  methods: {
    ...Vuex.mapMutations('utils', ['hideBusinessAccountInformationDialog']),
    async handleOngoingOnboarding() {
      if (this.isSwanVerificationNotStarted) {
        const { data: identification } = await createIdentificationUrl()
        this.onboardingLink = identification.redirect_url
        return
      }
      const { data: authorization } = await createAuthorizationUrl()
      this.onboardingLink = authorization.redirect_url
    },
    showSwanInformationForm() {
      if (this.hasRestrictions) {
        this.showRestrictionDialog({
          title: this.$t('business_account.cannot_open_account')
        })
        return
      }

      this.showOnboardingForm = true
    },
    closeDialog() {
      this.hideBusinessAccountInformationDialog()
    },
    openExistingOnboarding() {
      window.open(this.onboardingLink, '_self')
    }
  }
}
</script>

<template>
  <BaseCustomModal>
    <div class="bg-custom-light-dark flex max-w-5xl flex-col text-white">
      <div class="sticky top-0 z-10 flex justify-end">
        <button
          type="button"
          :aria-label="$t('general.close')"
          class="bg-custom-light-dark absolute mt-3 mr-3 size-8 rounded-full border border-gray-500 text-2xl leading-none text-primary hover:text-primary-200 lg:border-none"
          @click.stop="closeDialog"
        >
          &times;
        </button>
      </div>
      <BaseAdvertisement
        :heading="$t('business_account.dialog_advertisement.heading')"
        :image="
          require(
            `@/assets/images/business-account/ukko-business-account-2-${language}.png`
          )
        "
        image-position="right"
        class="hero mt-2 px-6"
        action="link"
        :href="$t('business_account.dialog_advertisement.advertisement_link')"
        image-class="max-w-full sm:max-w-64 lg:max-w-72"
      >
        <template #content-top>
          <div class="flex flex-row items-center gap-2">
            <VIcon
              class="rounded-full bg-blue p-1.5"
              size="2rem"
              icon="$bank-outline"
              color="white"
            />

            <h2 class="heading-4">
              {{ $t(`subscriptions.addons.payment_account.name`) }}
            </h2>
          </div>
        </template>
        <p>
          {{
            $t('business_account.dialog_advertisement.content.main_description')
          }}
        </p>
        <div>
          <p class="mb-1 font-semibold">
            {{
              $t(
                'business_account.dialog_advertisement.content.requirements_heading'
              )
            }}
          </p>
          <p v-for="(text, index) in requirementTexts" :key="index" class="">
            <span class="pr-4 text-primary">›</span>{{ text }}
          </p>
        </div>

        <template #action>
          {{ $t('business_account.dialog_advertisement.action') }}
        </template>
      </BaseAdvertisement>
      <div
        class="mb-4 flex flex-col gap-y-8 px-6 py-4 md:px-12 lg:flex-row lg:gap-x-8 lg:gap-y-0"
      >
        <div class="flex-1">
          <BaseIcon name="time-flies" class="mb-2 size-10" custom-size />
          <h3 class="heading-4 mb-2">
            {{ $t('business_account.dialog_advertisement.no_fees_title') }}
          </h3>
          <p>
            {{
              $t('business_account.dialog_advertisement.no_fees_description')
            }}
          </p>
        </div>
        <div class="flex-1">
          <BaseIcon name="multiple-cards" class="mb-2 size-10" custom-size />
          <h3 class="heading-4 mb-2">
            {{ $t('business_account.dialog_advertisement.price_title') }}
          </h3>
          <p>
            {{ $t('business_account.dialog_advertisement.price_description') }}
          </p>
        </div>
        <div class="flex-1">
          <BaseIcon name="money-sheet" class="mb-2 size-10" custom-size />
          <h3 class="heading-4 mb-2">
            {{ $t('business_account.dialog_advertisement.column3_title') }}
          </h3>
          <p>
            {{
              $t('business_account.dialog_advertisement.column3_description')
            }}
          </p>
        </div>
      </div>

      <Transition name="expand">
        <BusinessAccountOnboardingForm
          v-if="showOnboardingForm"
          class="onboarding-form px-6 pt-4 md:px-12"
        />
      </Transition>

      <div
        class="flex justify-center px-6 md:px-12"
        :class="zettleConnected ? 'mb-2' : 'mb-10'"
      >
        <LoadingSpinner v-if="isLoading" class="mr-2" />

        <BaseAlert v-else-if="isMobileAppAndIos">
          <p class="text-sm text-black">
            {{
              $t('business_account.dialog_advertisement.mobile_disclaimer_ios')
            }}
          </p>
        </BaseAlert>
        <BaseButton
          v-else-if="onboardingLink"
          filled
          class="rounded-full"
          @click.stop="openExistingOnboarding"
        >
          {{ $t('business_account.dialog_advertisement.continue_onboarding') }}
        </BaseButton>

        <div v-else-if="!showOnboardingForm" class="flex flex-col items-center">
          <BaseButton
            filled
            class="rounded-full"
            @click.stop="showSwanInformationForm"
          >
            {{ $t('business_account.dialog_advertisement.start_onboarding') }}
          </BaseButton>
        </div>
      </div>

      <div v-if="zettleConnected" class="mb-4 block text-center">
        <p class="text-xs text-white">
          {{ $t('business_account.dialog_advertisement.zettle_information') }}
        </p>
      </div>

      <div class="bg-custom-dark flex flex-col p-6 md:p-10">
        <div class="flex-1">
          <img
            :src="require(`@/assets/images/business-account/ukko-x-swan.png`)"
            class="mb-2"
            alt=""
          />
        </div>
        <div
          class="flex flex-1 flex-col gap-y-4 text-gray-300 md:flex-row md:gap-x-8 md:gap-y-0"
        >
          <div class="flex-1">
            <h4 class="mb-4 font-medium text-white">
              {{
                $t('business_account.dialog_advertisement.collaboration_title')
              }}
            </h4>
            <p class="text-xs">
              {{
                $t(
                  'business_account.dialog_advertisement.collaboration_description'
                )
              }}
            </p>
          </div>
          <div class="flex-1">
            <p class="mb-4 text-xs">
              {{ $t('business_account.dialog_advertisement.swan_info') }}
            </p>
            <p class="text-xs">
              {{ $t('business_account.dialog_advertisement.swan_info2') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </BaseCustomModal>
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

.bg-custom-dark {
  background-color: #272727;
}

.bg-custom-light-dark {
  background-color: #454545;
}

.hero :deep(h2) {
  @apply text-white;
}

.onboarding-form :deep(.input-label) {
  @apply text-white;
}

.onboarding-form :deep(p.error-message) {
  @apply text-danger-500;
}

.custom-modal :deep(button:disabled),
.custom-modal :deep(button.disabled) {
  @apply bg-secondary-600;
}

.expand-enter-active,
.expand-leave-active {
  @apply transition-all duration-200 ease-in-out overflow-hidden;
}

.expand-enter-from,
.expand-leave-to {
  @apply h-0 my-0 opacity-0;
}

:deep(input),
:deep(select) {
  @apply bg-white;
}
</style>
